import type { Conversation } from '@/types';

/**
 * WHO owns a conversation - the ONE derivation the chat header, the answer bar and the contact panel share, so the
 * three can never name different owners for the same row.
 *
 * It is the wire `ownerAgentId` and nothing else (ADR-033). There is no fallback to `assignedAgentId`: that field is
 * the live controller of a human-controlled conversation (and the addressee of a consultation, who owns nothing), not
 * the owner, and reading it as one is what let a stale row name the wrong person. The console renders what the server
 * says and never decides ownership.
 *
 * Returns null when nobody can be named - a blank id, `null` ("team shared") and `undefined` ("the server did not say")
 * all read the same here. Callers that need to decide who may ACT must treat an owner they cannot match to the
 * signed-in agent as SOMEONE ELSE, and an unknown owner during HUMAN_TAKEOVER as read-only - unknown fails closed,
 * never open.
 */
export function conversationOwnerId(conv: Pick<Conversation, 'ownerAgentId'>): string | null {
  return (conv.ownerAgentId ?? '').trim() || null;
}

/**
 * Is private ownership IN EFFECT for this row? The server's own statement (`ownershipEnforced`, ADR-033), read from the
 * wire and nowhere else; absent means false. While it is false (every tenant until PR-A2 is deployed AND armed) a
 * recorded owner is only a record - it locks nobody out of the conversation and earns no privacy badge - so the three
 * ownership consumers (chat header, answer bar, contact panel) apply "someone else owns it" ONLY when this is true.
 * Unknown-owner-during-takeover is a different, always-on rule and does not read this.
 */
export function isOwnershipEnforced(conv: Pick<Conversation, 'ownershipEnforced'>): boolean {
  return conv.ownershipEnforced === true;
}

/**
 * Does this row say NOTHING about who owns it - so the console cannot tell whether it is the viewer's to act on?
 * True for a row synthesised from a push or a draft placeholder (`partial`: it has never been read from the server), and
 * for a row the server answered under private ownership WITHOUT stating an owner (`ownershipEnforced` true and
 * `ownerAgentId` absent - team-shared is an explicit `null`, never an absence). Unknown fails closed: every consumer
 * that decides who may ACT (answer bar, Take over, Close, Spam, tag editor, the contact panel's editors) treats it as
 * read-only until the row has been re-read, whatever the state of the conversation. Deliberately independent of
 * `lockedByOtherOwner`, which is about a NAMED owner.
 */
export function isOwnerUnknown(conv: Pick<Conversation, 'partial' | 'ownershipEnforced' | 'ownerAgentId'>): boolean {
  if (conv.partial === true) return true;
  return isOwnershipEnforced(conv) && conv.ownerAgentId === undefined;
}

/**
 * The live CONTROLLER of the row: `assignedAgentId`, trimmed, or null. In HUMAN_TAKEOVER it is the human who holds the
 * customer right now; in other states it is only an addressee (a routed consultation) or nothing. It is NOT an owner -
 * {@link conversationOwnerId} never reads it - and it is read for exactly one purpose: who holds a live takeover while
 * private ownership is not in effect (see {@link holderId}).
 */
export function controllerId(conv: Pick<Conversation, 'assignedAgentId'>): string | null {
  return (conv.assignedAgentId ?? '').trim() || null;
}

/**
 * WHO HOLDS this conversation for the purpose of "may I act on it" - the person whose conversation it is right now, as
 * the row says it (the caller verifies it; see console/useVerifiedOwner.ts). Under private ownership (`ownershipEnforced`)
 * that is the OWNER. In legacy shared mode it is the live CONTROLLER (`assignedAgentId`), NOT the recorded owner: PR-A1
 * only records a claim on an UNOWNED row and refuses nothing, so when agent B takes over a conversation agent A
 * guided, the owner stays A while B is the one holding the customer. Keying the lock on the owner there would give B a
 * read-only bar and A a live composer on B's customer - exactly the collision the lock exists to prevent.
 *
 * Null = nobody can be named. During HUMAN_TAKEOVER that is UNKNOWN and fails closed in every consumer
 * (answer bar, Take over, Close, Spam, tag editor, contact panel editors).
 */
export function holderId(conv: Pick<Conversation, 'ownershipEnforced' | 'ownerAgentId' | 'assignedAgentId'>): string | null {
  return isOwnershipEnforced(conv) ? conversationOwnerId(conv) : controllerId(conv);
}

/**
 * Does a NAMED owner who is not the signed-in agent lock this viewer out of acting on the row? The ONE rule the chat
 * header, the answer bar, the contact panel and the folder helpers share.
 *
 *  - private ownership in effect (`ownershipEnforced`): always - someone else's conversation is read-only to me;
 *  - not in effect (legacy shared mode): only while a human holds the conversation (HUMAN_TAKEOVER). A recorded owner of
 *    a bot-handled or consulted conversation is just a record there (PR-A1 records one on every guidance and every draft
 *    approval and refuses nothing), but a live human takeover by a teammate is the controller of that customer, and two
 *    people writing to one customer is not "shared", it is a collision - the same lock the console has always had.
 *
 * `owner` is the VERIFIED HOLDER ({@link holderId}: the owner under private ownership, the live controller in legacy
 * shared mode; null when nobody can be named or it is not yet confirmed - never a lock by itself; the
 * unknown-holder-during-takeover rule is separate and always on). A blank or absent `me` is nobody.
 */
export function lockedByOtherOwner(
  conv: Pick<Conversation, 'ownershipEnforced' | 'state'>,
  owner: string | null | undefined,
  me: string | null | undefined,
): boolean {
  if (!owner) return false;
  if (!!me && owner === me) return false;
  return isOwnershipEnforced(conv) || conv.state === 'HUMAN_TAKEOVER';
}

/** What an owner reads as on screen. `title` carries the raw id only when no name could be found for it. */
export interface OwnerDisplay {
  text: string;
  title?: string;
  mine: boolean;
}

/**
 * The ONE owner-name rule the contact panel's "Owner" row and the chat header share (PR-A): 'You' for the signed-in
 * agent, the directory name for a teammate, the generic agent label (id in the tooltip) for an id the directory cannot
 * resolve - never a raw GUID as text - and 'Team shared' when nobody is named. Callers decide WHEN an owner is known
 * enough to show (an unverified owner is the caller's concern); this only names one.
 */
export function ownerDisplay(
  ownerId: string | null | undefined,
  me: string | null | undefined,
  directory: Readonly<Record<string, string>>,
  t: (key: 'cp_owner_team' | 'team_you' | 'agentFallbackName') => string,
): OwnerDisplay {
  const id = (ownerId ?? '').trim();
  if (!id) return { text: t('cp_owner_team'), mine: false };
  if (me && id === me) return { text: t('team_you'), mine: true };
  if (directory[id]) return { text: directory[id], mine: false };
  return { text: t('agentFallbackName'), title: id, mine: false };
}
